// src/components/References.jsx
// References tab showing project images from referenceImages table

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  getAllReferenceImages,
  updateProjectCodes,
  updateImageProjectCode,
  updateImageDescription,
  updateNextcloudPath,
  deleteReferenceImage,
  deleteMultipleReferenceImages,
} from "../services/azureReferenceImagesService.js";
import { getAllProjects } from "../services/azureProjectsService.js";
import { useAuth } from "../hooks/useAuth";
import { ROLES, hasMinimumRole } from "../utils/permissions";
import { useFreelancerProjects } from "../hooks/useFreelancerProjects";
import { useNextcloud } from "../hooks/useNextcloud";
import {
  uploadReferenceImage,
  uploadMultipleReferenceImages,
  getLocalPath,
  getLocalPathAsFileUrl,
} from "../services/nextcloudService.js";
import "../styles/_references.scss";

const References = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [allImages, setAllImages] = useState([]);
  const [projectColors, setProjectColors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [initialProjectId, setInitialProjectId] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxCode, setLightboxCode] = useState("");
  const [savingCode, setSavingCode] = useState(false);
  // Zoom state for lightbox
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  // Track which image is being edited (by rowKey) and its input value
  const [editingImageKey, setEditingImageKey] = useState(null);
  const [editingCode, setEditingCode] = useState("");
  // Modal for image description
  const [descriptionModal, setDescriptionModal] = useState(null);
  const [imageDescription, setImageDescription] = useState("");
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Project-level code management
  const [projectCode, setProjectCode] = useState("");
  const [savingProjectCode, setSavingProjectCode] = useState(false);

  // Freelancer project filtering
  const { assignedProjects, isFreelancerUser } = useFreelancerProjects(user);

  // Nextcloud integration
  const { isConnected: ncConnected } = useNextcloud();
  const [ncUploading, setNcUploading] = useState(null); // rowKey of image currently uploading
  const [ncProjectUploading, setNcProjectUploading] = useState(false);
  const [ncProgress, setNcProgress] = useState({ current: 0, total: 0, fileName: "" });
  const [ncResult, setNcResult] = useState(null);
  const [ncToast, setNcToast] = useState(null); // { message, type: "success"|"error" }

  // Check if user can delete (admin or PL)
  const canDelete = useMemo(() => {
    if (!user?.userRoles) return false;
    return user.userRoles.includes(ROLES.ADMIN) || user.userRoles.includes(ROLES.PL);
  }, [user]);

  // Check if user can use Nextcloud (Admin, PL, or Team -- not Freelancer)
  const canNextcloud = useMemo(() => ncConnected && hasMinimumRole(user, ROLES.TEAM), [ncConnected, user]);

  // Filter images for freelancers - only show images with assigned project codes
  const filteredImages = useMemo(() => {
    if (!isFreelancerUser || assignedProjects.length === 0) {
      return allImages;
    }

    // Filter images that have at least one project code matching freelancer's assigned projects
    return allImages.filter((image) => {
      // Check ProjectCodes field (project-level codes)
      const projectCodes = image.projectCodes
        ? image.projectCodes.split(";").map((c) => c.trim()).filter((c) => c)
        : [];
      // Check ImageProjectCode field (image-specific codes)
      const imageCodes = image.imageProjectCode
        ? image.imageProjectCode.split(";").map((c) => c.trim()).filter((c) => c)
        : [];

      const allCodes = [...new Set([...projectCodes, ...imageCodes])];

      // Return true if any code matches assigned projects
      return allCodes.some((code) => assignedProjects.includes(code));
    });
  }, [allImages, isFreelancerUser, assignedProjects]);

  // Parse URL params on mount to get initial project
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectParam = params.get("project");
    if (projectParam) {
      setInitialProjectId(decodeURIComponent(projectParam));
    }
  }, []);

  // Load all reference images and project colors on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load images and projects in parallel
        const [images, projects] = await Promise.all([
          getAllReferenceImages(),
          getAllProjects().catch(() => []) // Don't fail if projects don't load
        ]);

        setAllImages(images);

        // Build project color map by project code
        const colorMap = {};
        projects.forEach(project => {
          if (project.projektNr && project.color) {
            colorMap[project.projektNr] = project.color;
          }
        });
        setProjectColors(colorMap);
      } catch (err) {
        console.error("Error loading reference images:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Group images by project (Office + Name) - uses filteredImages for freelancers
  const projects = useMemo(() => {
    const projectsMap = new Map();

    filteredImages.forEach((image) => {
      const key = `${image.office}|||${image.name}`;

      if (!projectsMap.has(key)) {
        projectsMap.set(key, {
          id: key,
          office: image.office,
          name: image.name,
          location: image.location,
          images: [],
          tags: new Set(),
          projectCodes: new Set(),
        });
      }

      const project = projectsMap.get(key);
      project.images.push(image);

      // Parse and collect project codes (semicolon-delimited)
      if (image.projectCodes) {
        const codeList = image.projectCodes.split(";").map((c) => c.trim()).filter((c) => c);
        codeList.forEach((code) => project.projectCodes.add(code));
      }

      // Parse and collect tags (semicolon-delimited)
      if (image.tags) {
        const tagList = image.tags.split(";").map((t) => t.trim()).filter((t) => t);
        tagList.forEach((tag) => project.tags.add(tag));
      }
    });

    // Convert to array and process
    const projectList = Array.from(projectsMap.values()).map((project) => ({
      ...project,
      tags: Array.from(project.tags),
      projectCodes: Array.from(project.projectCodes),
      images: project.images.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      ),
      firstImage: project.images.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      )[0],
    }));

    // Sort projects by office name
    return projectList.sort((a, b) => a.office.localeCompare(b.office));
  }, [filteredImages]);

  // Select project from URL once projects are loaded
  useEffect(() => {
    if (initialProjectId && projects.length > 0 && !selectedProject) {
      const projectFromUrl = projects.find(p => p.id === initialProjectId);
      if (projectFromUrl) {
        setSelectedProject(projectFromUrl);
      }
      setInitialProjectId(null); // Clear so we don't re-trigger
    }
  }, [initialProjectId, projects, selectedProject]);

  // Keep selectedProject in sync with updated projects list (when images are added/deleted/modified)
  useEffect(() => {
    if (selectedProject) {
      const updatedProject = projects.find(p => p.id === selectedProject.id);
      if (updatedProject) {
        // Always update to get latest image data (project codes, descriptions, etc.)
        setSelectedProject(updatedProject);
      } else {
        // Project no longer exists (all images deleted)
        setSelectedProject(null);
      }
    }
  }, [projects]);

  // Filter and create mixed grid of projects and individual images
  // Supports multi-word AND filtering: "Wood Housing 014" matches items with ALL terms
  const { filteredProjects, individualImages } = useMemo(() => {
    if (!searchTerm.trim()) {
      return { filteredProjects: projects, individualImages: [] };
    }

    // Split search term into individual words for AND filtering
    const searchTerms = searchTerm.toLowerCase().split(/\s+/).filter(t => t);

    // Helper to check if text matches ALL search terms
    const matchesAllTerms = (text) => {
      if (!text) return false;
      const lowerText = text.toLowerCase();
      return searchTerms.every(term => lowerText.includes(term));
    };

    // Helper to check if any item in array matches ALL search terms
    const arrayMatchesAllTerms = (arr) => {
      if (!arr || arr.length === 0) return false;
      // Check if the combined array content matches all terms
      const combined = arr.join(" ").toLowerCase();
      return searchTerms.every(term => combined.includes(term));
    };

    // Helper to check if project matches all search terms (across all fields)
    const projectMatchesAllTerms = (project) => {
      // Build searchable content from all project fields
      const searchableFields = [
        project.office || "",
        project.name || "",
        project.location || "",
        ...project.tags,
        ...project.projectCodes,
      ].join(" ").toLowerCase();

      return searchTerms.every(term => searchableFields.includes(term));
    };

    // Helper to check if image matches all search terms
    const imageMatchesAllTerms = (image, project) => {
      const imageCodes = image.imageProjectCode
        ? image.imageProjectCode.split(";").map(c => c.trim())
        : [];

      const searchableFields = [
        project.office || "",
        project.name || "",
        project.location || "",
        ...project.tags,
        ...project.projectCodes,
        ...imageCodes,
      ].join(" ").toLowerCase();

      return searchTerms.every(term => searchableFields.includes(term));
    };

    const projectCodeMatches = [];
    const imageCodeMatches = [];
    const otherMatches = [];
    const matchingIndividualImages = [];

    projects.forEach((project) => {
      // Check if ProjectCodes match all terms
      const hasProjectCodeMatch = searchTerms.every(term =>
        project.projectCodes.some(code => code.toLowerCase().includes(term))
      );

      // Find individual images with matching ImageProjectCode for all terms
      const imagesWithMatchingCode = project.images.filter((img) => {
        if (!img.imageProjectCode) return false;
        const codes = img.imageProjectCode.split(";").map((c) => c.trim().toLowerCase());
        return searchTerms.every(term => codes.some(code => code.includes(term)));
      });

      // Always add individual images with matching codes (regardless of project match)
      if (imagesWithMatchingCode.length > 0) {
        imagesWithMatchingCode.forEach((img) => {
          matchingIndividualImages.push({
            ...img,
            projectOffice: project.office,
            projectName: project.name,
          });
        });
      }

      // Check if project matches all terms across any fields
      const hasAllTermsMatch = projectMatchesAllTerms(project);

      if (hasProjectCodeMatch) {
        projectCodeMatches.push(project);
      } else if (hasAllTermsMatch) {
        otherMatches.push(project);
      }
    });

    return {
      filteredProjects: [...projectCodeMatches, ...otherMatches],
      individualImages: matchingIndividualImages,
    };
  }, [projects, searchTerm]);

  // Get images for current view (either selected project or individual images from search)
  const currentImages = useMemo(() => {
    if (selectedProject) {
      return selectedProject.images;
    }
    // When in overview with search results, use individual images for lightbox navigation
    if (individualImages.length > 0) {
      return individualImages;
    }
    return [];
  }, [selectedProject, individualImages]);

  // Get color for a project code
  const getCodeColor = useCallback((code) => {
    return projectColors[code] || "#c44536";
  }, [projectColors]);

  // Handle project click
  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project);
  }, []);

  // Handle back to grid
  const handleBackToGrid = useCallback(() => {
    setSelectedProject(null);
    setSyncMode(false);
    setSelectedImages(new Set());
    setSyncResult(null);
  }, []);

  // Handle image click to open lightbox
  const handleImageClick = useCallback((image, index, e) => {
    e.stopPropagation();
    setLightboxImage(image);
    setLightboxIndex(index);
    setLightboxCode("");
    // Clear grid editing state
    setEditingImageKey(null);
    setEditingCode("");
  }, []);

  // Handle individual image click from overview grid (stay in overview mode)
  const handleIndividualImageClick = useCallback((image, index, e) => {
    e.stopPropagation();
    // Don't navigate to project - stay in overview and use individual images for lightbox
    setLightboxImage(image);
    setLightboxIndex(index);
    setLightboxCode("");
    // Clear grid editing state
    setEditingImageKey(null);
    setEditingCode("");
  }, []);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    setLightboxIndex(0);
    setLightboxCode("");
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  // Reset zoom when navigating images
  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  }, []);

  // Zoom controls
  const handleZoomIn = useCallback((e) => {
    e?.stopPropagation();
    setZoomLevel((prev) => Math.min(prev + 0.5, 5));
  }, []);

  const handleZoomOut = useCallback((e) => {
    e?.stopPropagation();
    setZoomLevel((prev) => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPanPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleZoomReset = useCallback((e) => {
    e?.stopPropagation();
    resetZoom();
  }, [resetZoom]);

  // Handle mouse wheel zoom in lightbox
  const handleWheelZoom = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY < 0) {
      setZoomLevel((prev) => Math.min(prev + 0.25, 5));
    } else {
      setZoomLevel((prev) => {
        const newZoom = Math.max(prev - 0.25, 1);
        if (newZoom === 1) {
          setPanPosition({ x: 0, y: 0 });
        }
        return newZoom;
      });
    }
  }, []);

  // Handle panning when zoomed in
  const handlePanStart = useCallback((e) => {
    if (zoomLevel > 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    }
  }, [zoomLevel, panPosition]);

  const handlePanMove = useCallback((e) => {
    if (isPanning && zoomLevel > 1) {
      e.preventDefault();
      setPanPosition({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  }, [isPanning, zoomLevel, panStart]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Navigate to previous image in lightbox
  const goToPreviousImage = useCallback(() => {
    if (currentImages.length === 0) return;
    const newIndex = lightboxIndex > 0 ? lightboxIndex - 1 : currentImages.length - 1;
    setLightboxIndex(newIndex);
    setLightboxImage(currentImages[newIndex]);
    setLightboxCode("");
    resetZoom();
  }, [currentImages, lightboxIndex, resetZoom]);

  // Navigate to next image in lightbox
  const goToNextImage = useCallback(() => {
    if (currentImages.length === 0) return;
    const newIndex = lightboxIndex < currentImages.length - 1 ? lightboxIndex + 1 : 0;
    setLightboxIndex(newIndex);
    setLightboxImage(currentImages[newIndex]);
    setLightboxCode("");
    resetZoom();
  }, [currentImages, lightboxIndex, resetZoom]);

  // Handle keyboard events for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isInputFocused = e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA";

      if (descriptionModal) {
        if (e.key === "Escape") {
          setDescriptionModal(null);
          setImageDescription("");
        }
        return;
      }

      if (e.key === "Escape") {
        if (syncMode) {
          handleToggleSyncMode();
        } else if (lightboxImage) {
          closeLightbox();
        } else if (selectedProject) {
          handleBackToGrid();
        }
      } else if (lightboxImage && !isInputFocused) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          goToPreviousImage();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          goToNextImage();
        } else if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          handleZoomIn();
        } else if (e.key === "-") {
          e.preventDefault();
          handleZoomOut();
        } else if (e.key === "0") {
          e.preventDefault();
          handleZoomReset();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImage, selectedProject, descriptionModal, closeLightbox, handleBackToGrid, goToPreviousImage, goToNextImage, handleZoomIn, handleZoomOut, handleZoomReset]);

  // Get the best available image URL
  const getImageUrl = (image) => {
    if (image.originalUrl) return image.originalUrl;
    if (image.sharePointUrl) return image.sharePointUrl;
    if (image.webSourceUrl) return image.webSourceUrl;
    if (image.oneDrivePath) return image.oneDrivePath;
    return null;
  };

  // Parse ImageProjectCode into array
  const parseImageProjectCodes = (imageProjectCode) => {
    if (!imageProjectCode) return [];
    return imageProjectCode.split(";").map((c) => c.trim()).filter((c) => c);
  };

  // Add project code to an image (isFromLightbox determines which state to clear)
  const addProjectCodeToImage = useCallback(async (image, code, isFromLightbox = false) => {
    if (!code.trim()) return;

    const trimmedCode = code.trim();
    const existingCodes = parseImageProjectCodes(image.imageProjectCode);

    // Don't add duplicate
    if (existingCodes.includes(trimmedCode)) {
      if (isFromLightbox) {
        setLightboxCode("");
      } else {
        setEditingCode("");
        setEditingImageKey(null);
      }
      return;
    }

    const newCodes = [...existingCodes, trimmedCode].join(";");

    // Immediately update local state for instant feedback
    const updateImageInState = (img) =>
      img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
        ? { ...img, imageProjectCode: newCodes }
        : img;

    setAllImages((prev) => prev.map(updateImageInState));

    // Update lightbox image if it's the same
    if (lightboxImage && lightboxImage.partitionKey === image.partitionKey && lightboxImage.rowKey === image.rowKey) {
      setLightboxImage({ ...image, imageProjectCode: newCodes });
    }

    // Clear the appropriate input immediately
    if (isFromLightbox) {
      setLightboxCode("");
    } else {
      setEditingCode("");
      setEditingImageKey(null);
    }

    try {
      setSavingCode(true);
      await updateImageProjectCode(image.partitionKey, image.rowKey, newCodes);
    } catch (err) {
      console.error("Failed to add project code:", err);
      // Revert on error
      setAllImages((prev) =>
        prev.map((img) =>
          img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
            ? { ...img, imageProjectCode: image.imageProjectCode }
            : img
        )
      );
      alert("Fehler beim Speichern des Projektcodes");
    } finally {
      setSavingCode(false);
    }
  }, [lightboxImage]);

  // Remove project code from an image
  const removeProjectCodeFromImage = useCallback(async (image, codeToRemove) => {
    const existingCodes = parseImageProjectCodes(image.imageProjectCode);
    const newCodes = existingCodes.filter((c) => c !== codeToRemove).join(";");

    // Immediately update local state
    setAllImages((prev) =>
      prev.map((img) =>
        img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
          ? { ...img, imageProjectCode: newCodes }
          : img
      )
    );

    // Update lightbox image if it's the same
    if (lightboxImage && lightboxImage.partitionKey === image.partitionKey && lightboxImage.rowKey === image.rowKey) {
      setLightboxImage({ ...image, imageProjectCode: newCodes });
    }

    try {
      setSavingCode(true);
      await updateImageProjectCode(image.partitionKey, image.rowKey, newCodes);
    } catch (err) {
      console.error("Failed to remove project code:", err);
      // Revert on error
      setAllImages((prev) =>
        prev.map((img) =>
          img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
            ? { ...img, imageProjectCode: image.imageProjectCode }
            : img
        )
      );
      alert("Fehler beim Entfernen des Projektcodes");
    } finally {
      setSavingCode(false);
    }
  }, [lightboxImage]);

  // Parse ProjectCodes into array (project-level codes)
  const parseProjectCodes = (projectCodesStr) => {
    if (!projectCodesStr) return [];
    return projectCodesStr.split(";").map((c) => c.trim()).filter((c) => c);
  };

  // Add project code to all images in a project (updates ProjectCodes field)
  const addProjectCodeToProject = useCallback(async (project, code) => {
    if (!code.trim()) return;

    const trimmedCode = code.trim();
    setSavingProjectCode(true);

    try {
      // Update ProjectCodes field on all images in the project
      const updatePromises = project.images.map(async (image) => {
        const existingCodes = parseProjectCodes(image.projectCodes);
        // Skip if already has this code
        if (existingCodes.includes(trimmedCode)) return null;

        const newCodes = [...existingCodes, trimmedCode].join(";");
        await updateProjectCodes(image.partitionKey, image.rowKey, newCodes);
        return { image, newCodes };
      });

      const results = await Promise.all(updatePromises);

      // Update local state for all affected images
      setAllImages((prev) =>
        prev.map((img) => {
          const result = results.find(
            (r) => r && r.image.partitionKey === img.partitionKey && r.image.rowKey === img.rowKey
          );
          if (result) {
            return { ...img, projectCodes: result.newCodes };
          }
          return img;
        })
      );

      setProjectCode("");
    } catch (err) {
      console.error("Failed to add project code to project:", err);
      alert("Fehler beim Hinzufügen des Projektcodes");
    } finally {
      setSavingProjectCode(false);
    }
  }, []);

  // Remove project code from all images in a project (updates ProjectCodes field)
  const removeProjectCodeFromProject = useCallback(async (project, codeToRemove) => {
    setSavingProjectCode(true);

    try {
      // Update ProjectCodes field on all images in the project
      const updatePromises = project.images.map(async (image) => {
        const existingCodes = parseProjectCodes(image.projectCodes);
        // Skip if doesn't have this code
        if (!existingCodes.includes(codeToRemove)) return null;

        const newCodes = existingCodes.filter((c) => c !== codeToRemove).join(";");
        await updateProjectCodes(image.partitionKey, image.rowKey, newCodes);
        return { image, newCodes };
      });

      const results = await Promise.all(updatePromises);

      // Update local state for all affected images
      setAllImages((prev) =>
        prev.map((img) => {
          const result = results.find(
            (r) => r && r.image.partitionKey === img.partitionKey && r.image.rowKey === img.rowKey
          );
          if (result) {
            return { ...img, projectCodes: result.newCodes };
          }
          return img;
        })
      );
    } catch (err) {
      console.error("Failed to remove project code from project:", err);
      alert("Fehler beim Entfernen des Projektcodes");
    } finally {
      setSavingProjectCode(false);
    }
  }, []);

  // Handle code tag click to open description modal
  const handleCodeTagClick = useCallback((e, image, code) => {
    e.stopPropagation();
    setDescriptionModal({ image, code });
    setImageDescription(image.imageDescription || "");
  }, []);

  // Handle tag click to add to search
  const handleTagClick = useCallback((e, tag) => {
    e.stopPropagation();
    // Add tag to search, or replace if already searching
    const currentTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(t => t);
    const tagLower = tag.toLowerCase();
    if (!currentTerms.includes(tagLower)) {
      setSearchTerm(prev => prev.trim() ? `${prev.trim()} ${tag}` : tag);
    }
    // Go back to overview if in detail view
    if (selectedProject) {
      setSelectedProject(null);
    }
  }, [searchTerm, selectedProject]);

  // Handle project code click to add to search (in overview)
  const handleProjectCodeClick = useCallback((e, code) => {
    e.stopPropagation();
    const currentTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(t => t);
    const codeLower = code.toLowerCase();
    if (!currentTerms.includes(codeLower)) {
      setSearchTerm(prev => prev.trim() ? `${prev.trim()} ${code}` : code);
    }
    if (selectedProject) {
      setSelectedProject(null);
    }
  }, [searchTerm, selectedProject]);

  // Delete a single image
  const handleDeleteImage = useCallback(async (image) => {
    if (!canDelete) return;
    setDeleteConfirm({ type: "image", image });
  }, [canDelete]);

  // Delete entire project (all images)
  const handleDeleteProject = useCallback(async (project) => {
    if (!canDelete) return;
    setDeleteConfirm({ type: "project", project });
  }, [canDelete]);

  // Confirm and execute delete
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      if (deleteConfirm.type === "image") {
        const { image } = deleteConfirm;
        await deleteReferenceImage(image.partitionKey, image.rowKey);
        // Remove from local state
        setAllImages(prev => prev.filter(
          img => !(img.partitionKey === image.partitionKey && img.rowKey === image.rowKey)
        ));
        // Close lightbox if deleting current image
        if (lightboxImage && lightboxImage.partitionKey === image.partitionKey && lightboxImage.rowKey === image.rowKey) {
          closeLightbox();
        }
      } else if (deleteConfirm.type === "project") {
        const { project } = deleteConfirm;
        const result = await deleteMultipleReferenceImages(project.images);
        if (result.totalFailed > 0) {
          alert(`${result.totalDeleted} Bilder gelöscht, ${result.totalFailed} fehlgeschlagen.`);
        }
        // Remove deleted images from local state
        const deletedKeys = new Set(
          result.results.map(r => `${r.image.partitionKey}|||${r.image.rowKey}`)
        );
        setAllImages(prev => prev.filter(
          img => !deletedKeys.has(`${img.partitionKey}|||${img.rowKey}`)
        ));
        // Go back to overview
        setSelectedProject(null);
        closeLightbox();
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Fehler beim Löschen: " + err.message);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  }, [deleteConfirm, lightboxImage, closeLightbox]);

  // Save image description
  const saveImageDescription = useCallback(async () => {
    if (!descriptionModal) return;

    const { image } = descriptionModal;
    const newDescription = imageDescription.trim();

    // Immediately update local state
    setAllImages((prev) =>
      prev.map((img) =>
        img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
          ? { ...img, imageDescription: newDescription }
          : img
      )
    );

    // Update lightbox image if it's the same
    if (lightboxImage && lightboxImage.partitionKey === image.partitionKey && lightboxImage.rowKey === image.rowKey) {
      setLightboxImage({ ...lightboxImage, imageDescription: newDescription });
    }

    // Close modal
    setDescriptionModal(null);
    setImageDescription("");

    try {
      await updateImageDescription(image.partitionKey, image.rowKey, newDescription);
    } catch (err) {
      console.error("Failed to save description:", err);
      // Revert on error
      setAllImages((prev) =>
        prev.map((img) =>
          img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
            ? { ...img, imageDescription: image.imageDescription }
            : img
        )
      );
      alert("Fehler beim Speichern der Beschreibung");
    }
  }, [descriptionModal, imageDescription, lightboxImage]);

  // === Nextcloud Handlers ===

  // Show a brief toast notification
  const showNcToast = useCallback((message, type = "success") => {
    setNcToast({ message, type });
    setTimeout(() => setNcToast(null), 3000);
  }, []);

  // Upload single image to Nextcloud
  const handleNcUploadSingle = useCallback(
    async (image, e) => {
      if (e) e.stopPropagation();
      setNcUploading(image.rowKey);
      try {
        const nextcloudPath = await uploadReferenceImage(image);
        await updateNextcloudPath(image.partitionKey, image.rowKey, nextcloudPath);
        setAllImages((prev) =>
          prev.map((img) =>
            img.partitionKey === image.partitionKey && img.rowKey === image.rowKey
              ? { ...img, nextcloudPath }
              : img
          )
        );
        showNcToast(`"${image.fileName || "Bild"}" in Nextcloud gespeichert`);
      } catch (err) {
        console.error("Nextcloud upload failed:", err);
        showNcToast(`Nextcloud-Fehler: ${err.message}`, "error");
      } finally {
        setNcUploading(null);
      }
    },
    [showNcToast]
  );

  // Open Explorer at path, copy path to clipboard as fallback
  const handleNcOpenPath = useCallback((image, e) => {
    if (e) e.stopPropagation();
    const localPath = getLocalPath(image.nextcloudPath);
    const fileUrl = getLocalPathAsFileUrl(image.nextcloudPath);

    // Try opening via file:// protocol (works in some browsers/configs)
    const opened = window.open(fileUrl, "_blank");

    // Always copy path to clipboard as fallback
    navigator.clipboard.writeText(localPath).then(() => {
      if (!opened || opened.closed) {
        showNcToast("Pfad kopiert — im Explorer einfuegen");
      } else {
        showNcToast("Ordner geoeffnet");
      }
    });
  }, [showNcToast]);

  // Upload all images of current project to Nextcloud
  const handleNcUploadProject = useCallback(async () => {
    if (!selectedProject) return;
    const images = selectedProject.images.filter((img) => !img.nextcloudPath);
    if (images.length === 0) return;

    setNcProjectUploading(true);
    setNcProgress({ current: 0, total: images.length, fileName: "" });
    setNcResult(null);

    try {
      const result = await uploadMultipleReferenceImages(images, (current, total, fileName) => {
        setNcProgress({ current, total, fileName });
      });

      // Persist paths for succeeded uploads
      for (const item of result.succeeded) {
        try {
          await updateNextcloudPath(item.image.partitionKey, item.image.rowKey, item.nextcloudPath);
          setAllImages((prev) =>
            prev.map((img) =>
              img.partitionKey === item.image.partitionKey && img.rowKey === item.image.rowKey
                ? { ...img, nextcloudPath: item.nextcloudPath }
                : img
            )
          );
        } catch (dbErr) {
          console.error("DB update failed for", item.image.fileName, dbErr);
          result.failed.push({
            image: item.image,
            error: `Upload OK, DB-Update fehlgeschlagen: ${dbErr.message}`,
          });
        }
      }

      setNcResult(result);
    } catch (err) {
      console.error("Nextcloud project upload error:", err);
      setNcResult({ succeeded: [], failed: [{ image: null, error: err.message }] });
    } finally {
      setNcProjectUploading(false);
    }
  }, [selectedProject]);

  // Handle key press in lightbox project code input
  const handleLightboxCodeKeyPress = useCallback((e, image) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addProjectCodeToImage(image, lightboxCode, true);
    }
  }, [addProjectCodeToImage, lightboxCode]);

  // Render loading state
  if (loading) {
    return (
      <div className="references-container">
        <div className="references-loading">
          <div className="spinner" />
          <p>Referenzen werden geladen...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="references-container">
        <div className="references-error">
          <p>Fehler beim Laden der Referenzen: {error}</p>
        </div>
      </div>
    );
  }

  // Calculate total count
  const totalCount = filteredProjects.length + individualImages.length;

  return (
    <div className="references-container">
      {/* Search Bar */}
      <div className="references-search">
        <input
          type="text"
          placeholder="Suche nach Office, Name, Ort, Tags oder Projektcode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button
            className="clear-search"
            onClick={() => setSearchTerm("")}
            title="Suche löschen"
          >
            ×
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="references-count">
        {filteredProjects.length} {filteredProjects.length === 1 ? "Projekt" : "Projekte"}
        {individualImages.length > 0 && ` + ${individualImages.length} Einzelbilder`}
        {searchTerm && ` für "${searchTerm}"`}
      </div>

      {/* Back Button when viewing project details */}
      {selectedProject && (
        <div className="references-back">
          <button onClick={handleBackToGrid} className="back-button">
            ← Zurück zur Übersicht
          </button>
          <h2 className="selected-project-title">
            <span className="project-office">{selectedProject.office}</span>
            {selectedProject.location && (
              <span className="project-location-header">| {selectedProject.location}</span>
            )}
            <span className="project-name">{selectedProject.name}</span>
          </h2>
          {canDelete && (
            <button
              className="delete-collection-btn"
              onClick={() => handleDeleteProject(selectedProject)}
              title="Alle Bilder dieses Projekts löschen"
            >
              🗑 Projekt löschen
            </button>
          )}
          {canNextcloud && (
            <button
              className="nc-upload-project-btn"
              onClick={handleNcUploadProject}
              disabled={ncProjectUploading}
              title="Alle Bilder in Nextcloud speichern"
            >
              {ncProjectUploading
                ? `Nextcloud (${ncProgress.current}/${ncProgress.total})`
                : "Alle in Nextcloud speichern"}
            </button>
          )}
        </div>
      )}

      {/* Project Grid or Project Detail View */}
      {selectedProject ? (
        // Detail View - Show all images of selected project
        <div className="references-detail">
          <div className="project-info">
            {selectedProject.location && (
              <span className="project-location">{selectedProject.location}</span>
            )}
            {selectedProject.tags.length > 0 && (
              <div className="project-tags">
                {selectedProject.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="tag clickable"
                    onClick={(e) => handleTagClick(e, tag)}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="project-codes-section">
              <span className="codes-label">Projektcodes:</span>
              {selectedProject.projectCodes.map((code, index) => (
                <span
                  key={index}
                  className="project-code-tag"
                  style={{ backgroundColor: getCodeColor(code) }}
                >
                  <span
                    className="code-text clickable"
                    onClick={(e) => handleProjectCodeClick(e, code)}
                  >
                    {code}
                  </span>
                  <button
                    className="remove-project-code"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeProjectCodeFromProject(selectedProject, code);
                    }}
                    disabled={savingProjectCode}
                    title="Code von allen Bildern entfernen"
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="project-code-input-field"
                placeholder="Neuen Projektcode hinzufügen..."
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addProjectCodeToProject(selectedProject, projectCode);
                  }
                }}
                disabled={savingProjectCode}
              />
              <button
                className="add-project-code-btn"
                onClick={() => addProjectCodeToProject(selectedProject, projectCode)}
                disabled={savingProjectCode || !projectCode.trim()}
              >
                {savingProjectCode ? "..." : "+ Hinzufügen"}
              </button>
            </div>
          </div>
          <div className="images-grid detail-grid">
            {selectedProject.images.map((image, index) => {
              const imageUrl = getImageUrl(image);
              const imageCodes = parseImageProjectCodes(image.imageProjectCode);
              const imageKey = image.rowKey || `${image.partitionKey}-${index}`;
              const isEditing = editingImageKey === imageKey;
              return (
                <div
                  key={imageKey}
                  className="image-card"
                  onClick={(e) => handleImageClick(image, index, e)}
                >
                  {canDelete && (
                    <button
                      className="delete-image-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image);
                      }}
                      title="Bild löschen"
                    >
                      🗑
                    </button>
                  )}
                  {canNextcloud && (
                    image.nextcloudPath ? (
                      <button
                        className="nc-copy-path-btn"
                        onClick={(e) => handleNcOpenPath(image, e)}
                        title="Nextcloud-Pfad kopieren"
                      >
                        Pfad kopieren
                      </button>
                    ) : (
                      <button
                        className="nc-upload-btn"
                        onClick={(e) => handleNcUploadSingle(image, e)}
                        disabled={ncUploading === image.rowKey}
                        title="In Nextcloud speichern"
                      >
                        {ncUploading === image.rowKey ? "..." : "NC"}
                      </button>
                    )
                  )}
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={image.fileName || image.name}
                      loading="lazy"
                    />
                  ) : (
                    <div className="no-image">
                      <span>Kein Bild verfügbar</span>
                    </div>
                  )}
                  <div className="image-info">
                    <div className="image-info-row">
                      <span className="file-name">{image.fileName}</span>
                      <input
                        type="text"
                        className="project-code-input"
                        placeholder="+ Code"
                        value={isEditing ? editingCode : ""}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          setEditingImageKey(imageKey);
                          setEditingCode(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addProjectCodeToImage(image, editingCode, false);
                          }
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                          setEditingImageKey(imageKey);
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            if (editingImageKey === imageKey && !savingCode) {
                              setEditingImageKey(null);
                              setEditingCode("");
                            }
                          }, 200);
                        }}
                        disabled={savingCode}
                      />
                    </div>
                    {imageCodes.length > 0 && (
                      <div className="image-project-codes">
                        {imageCodes.map((code, idx) => (
                          <span
                            key={idx}
                            className="image-code"
                            style={{ backgroundColor: getCodeColor(code) }}
                            onClick={(e) => handleCodeTagClick(e, image, code)}
                          >
                            {code}
                            <button
                              className="remove-code"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeProjectCodeFromImage(image, code);
                              }}
                              disabled={savingCode}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Source URLs Section */}
          {(() => {
            // Collect unique source website URLs from all images
            const sourceUrls = new Map();
            selectedProject.images.forEach((image) => {
              // Check for sourceWebsite (the source page URL where images were pulled from)
              if (image.sourceWebsite) {
                try {
                  const url = new URL(image.sourceWebsite);
                  const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;
                  if (!sourceUrls.has(baseUrl)) {
                    sourceUrls.set(baseUrl, {
                      url: baseUrl,
                      displayUrl: image.sourceWebsite,
                      host: url.host,
                    });
                  }
                } catch (e) {
                  // Invalid URL, use as-is
                  if (!sourceUrls.has(image.sourceWebsite)) {
                    sourceUrls.set(image.sourceWebsite, {
                      url: image.sourceWebsite,
                      displayUrl: image.sourceWebsite,
                      host: image.sourceWebsite,
                    });
                  }
                }
              }
            });

            const urlList = Array.from(sourceUrls.values());

            if (urlList.length === 0) return null;

            return (
              <div className="source-urls-section">
                <h3 className="source-urls-header">Bildquellen</h3>
                <ul className="source-urls-list">
                  {urlList.map((source, index) => (
                    <li key={index} className="source-url-item">
                      <a
                        href={source.displayUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="source-url-link"
                        title={source.displayUrl}
                      >
                        {source.host}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>
      ) : (
        // Grid View - Show projects and individual images in separate sections
        <div className="references-overview">
          {totalCount === 0 ? (
            <div className="no-results">
              <p>Keine Referenzen gefunden.</p>
            </div>
          ) : (
            <>
              {/* Projects Section */}
              {filteredProjects.length > 0 && (
                <div className="references-section">
                  {searchTerm && individualImages.length > 0 && (
                    <h3 className="section-header">Projekte ({filteredProjects.length})</h3>
                  )}
                  <div className="references-grid">
                    {filteredProjects.map((project) => {
                      const firstImage = project.firstImage;
                      const imageUrl = firstImage ? getImageUrl(firstImage) : null;

                      return (
                        <div
                          key={project.id}
                          className="project-card"
                          onClick={() => handleProjectClick(project)}
                        >
                          <div className="project-image">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={project.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="no-image">
                                <span>Kein Bild</span>
                              </div>
                            )}
                            <div className="image-count">
                              {project.images.length} {project.images.length === 1 ? "Bild" : "Bilder"}
                            </div>
                            {canDelete && (
                              <button
                                className="delete-project-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProject(project);
                                }}
                                title="Projekt löschen"
                              >
                                🗑
                              </button>
                            )}
                          </div>
                          <div className="project-details">
                            <div className="project-header">
                              <span className="office-name">{project.office}</span>
                              {project.location && (
                                <span className="location-separator">|</span>
                              )}
                              {project.location && (
                                <span className="project-location">{project.location}</span>
                              )}
                            </div>
                            <span className="project-name">{project.name}</span>
                            {project.tags.length > 0 && (
                              <div className="project-tags-row">
                                {project.tags.slice(0, 3).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="project-tag clickable"
                                    onClick={(e) => handleTagClick(e, tag)}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {project.tags.length > 3 && (
                                  <span className="project-tag more">+{project.tags.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Individual Images Section */}
              {individualImages.length > 0 && (
                <div className="references-section individual-images-section">
                  <h3 className="section-header">Einzelbilder mit passendem Projektcode ({individualImages.length})</h3>
                  <div className="references-grid">
                    {individualImages.map((image, index) => {
                      const imageUrl = getImageUrl(image);
                      const imageCodes = parseImageProjectCodes(image.imageProjectCode);
                      const imageKey = image.rowKey || `individual-${index}`;
                      const isEditing = editingImageKey === imageKey;

                      return (
                        <div
                          key={`individual-${image.rowKey || index}`}
                          className="project-card individual-image-card"
                          onClick={(e) => handleIndividualImageClick(image, index, e)}
                        >
                          <div className="project-image">
                            {imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={image.fileName || image.name}
                                loading="lazy"
                              />
                            ) : (
                              <div className="no-image">
                                <span>Kein Bild</span>
                              </div>
                            )}
                          </div>
                          <div className="project-details">
                            <span className="office-name">{image.projectOffice}</span>
                            <span className="project-name">{image.projectName}</span>
                            {image.imageDescription && (
                              <span className="image-description">{image.imageDescription}</span>
                            )}
                            <div className="image-codes-section">
                              {imageCodes.length > 0 && (
                                <div className="image-project-codes">
                                  {imageCodes.map((code, idx) => (
                                    <span
                                      key={idx}
                                      className="image-code"
                                      style={{ backgroundColor: getCodeColor(code) }}
                                      onClick={(e) => handleCodeTagClick(e, image, code)}
                                    >
                                      {code}
                                      <button
                                        className="remove-code"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeProjectCodeFromImage(image, code);
                                        }}
                                        disabled={savingCode}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              )}
                              <input
                                type="text"
                                className="project-code-input"
                                placeholder="+ Code"
                                value={isEditing ? editingCode : ""}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  setEditingImageKey(imageKey);
                                  setEditingCode(e.target.value);
                                }}
                                onKeyPress={(e) => {
                                  e.stopPropagation();
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addProjectCodeToImage(image, editingCode, false);
                                  }
                                }}
                                onFocus={(e) => {
                                  e.stopPropagation();
                                  setEditingImageKey(imageKey);
                                }}
                                onBlur={() => {
                                  setTimeout(() => {
                                    if (editingImageKey === imageKey && !savingCode) {
                                      setEditingImageKey(null);
                                      setEditingCode("");
                                    }
                                  }, 200);
                                }}
                                disabled={savingCode}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          {/* Left navigation area */}
          <div
            className="lightbox-nav lightbox-nav-left"
            onClick={(e) => {
              e.stopPropagation();
              goToPreviousImage();
            }}
          >
            <span className="nav-arrow">‹</span>
          </div>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            {canDelete && (
              <button
                className="lightbox-delete"
                onClick={() => handleDeleteImage(lightboxImage)}
                title="Bild löschen"
              >
                🗑
              </button>
            )}

            {/* Zoom Controls */}
            <div className="lightbox-zoom-controls">
              <button
                className="zoom-btn"
                onClick={handleZoomOut}
                disabled={zoomLevel <= 1}
                title="Verkleinern (-)"
              >
                −
              </button>
              <span className="zoom-level" onClick={handleZoomReset} title="Zurücksetzen (0)">
                {Math.round(zoomLevel * 100)}%
              </span>
              <button
                className="zoom-btn"
                onClick={handleZoomIn}
                disabled={zoomLevel >= 5}
                title="Vergrössern (+)"
              >
                +
              </button>
            </div>

            <div
              className={`lightbox-image-container ${zoomLevel > 1 ? 'zoomed' : ''}`}
              onWheel={handleWheelZoom}
              onMouseDown={handlePanStart}
              onMouseMove={handlePanMove}
              onMouseUp={handlePanEnd}
              onMouseLeave={handlePanEnd}
            >
              <img
                src={getImageUrl(lightboxImage)}
                alt={lightboxImage.fileName || lightboxImage.name}
                style={{
                  transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                  cursor: zoomLevel > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
                }}
                draggable={false}
              />
            </div>
            <div className="lightbox-info">
              <span className="file-name">{lightboxImage.fileName}</span>
              {lightboxImage.location && (
                <span className="location">{lightboxImage.location}</span>
              )}
              <div className="lightbox-counter">
                {lightboxIndex + 1} / {currentImages.length}
              </div>
            </div>

            {/* Project Code Section */}
            <div className="lightbox-project-codes">
              <div className="codes-list">
                {parseImageProjectCodes(lightboxImage.imageProjectCode).map((code, idx) => (
                  <span
                    key={idx}
                    className="image-code"
                    style={{ backgroundColor: getCodeColor(code) }}
                    onClick={(e) => handleCodeTagClick(e, lightboxImage, code)}
                  >
                    {code}
                    <button
                      className="remove-code"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeProjectCodeFromImage(lightboxImage, code);
                      }}
                      disabled={savingCode}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="code-input-row">
                <input
                  type="text"
                  className="lightbox-code-input"
                  placeholder="Projektcode hinzufügen..."
                  value={lightboxCode}
                  onChange={(e) => setLightboxCode(e.target.value)}
                  onKeyPress={(e) => handleLightboxCodeKeyPress(e, lightboxImage)}
                  disabled={savingCode}
                />
                <button
                  className="add-code-btn"
                  onClick={() => addProjectCodeToImage(lightboxImage, lightboxCode, true)}
                  disabled={savingCode || !lightboxCode.trim()}
                >
                  {savingCode ? "..." : "+"}
                </button>
              </div>
            </div>
          </div>

          {/* Right navigation area */}
          <div
            className="lightbox-nav lightbox-nav-right"
            onClick={(e) => {
              e.stopPropagation();
              goToNextImage();
            }}
          >
            <span className="nav-arrow">›</span>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {descriptionModal && (
        <div className="modal-overlay" onClick={() => setDescriptionModal(null)}>
          <div className="description-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bildbeschreibung für Code: {descriptionModal.code}</h3>
              <button className="modal-close" onClick={() => setDescriptionModal(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <textarea
                className="description-textarea"
                placeholder="Beschreibung oder Kommentar hinzufügen..."
                value={imageDescription}
                onChange={(e) => setImageDescription(e.target.value)}
                rows={4}
              />
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setDescriptionModal(null)}>
                Abbrechen
              </button>
              <button className="modal-btn save" onClick={saveImageDescription}>
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Löschen bestätigen</h3>
              <button
                className="modal-close"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {deleteConfirm.type === "image" ? (
                <p>
                  Möchten Sie das Bild <strong>{deleteConfirm.image.fileName}</strong> wirklich löschen?
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              ) : (
                <p>
                  Möchten Sie das gesamte Projekt <strong>{deleteConfirm.project.office} - {deleteConfirm.project.name}</strong> mit
                  allen <strong>{deleteConfirm.project.images.length} Bildern</strong> wirklich löschen?
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn cancel"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Abbrechen
              </button>
              <button
                className="modal-btn delete"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Lösche..." : "Löschen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Nextcloud Toast */}
      {ncToast && (
        <div
          className={`nc-toast ${ncToast.type}`}
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            zIndex: 9999,
            padding: "0.75rem 1.25rem",
            borderRadius: "8px",
            fontSize: "0.9rem",
            fontWeight: 500,
            color: "white",
            background: ncToast.type === "error" ? "#ef4444" : "#22c55e",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          {ncToast.message}
        </div>
      )}

      {/* Nextcloud Upload Progress Overlay */}
      {ncProjectUploading && (
        <div className="modal-overlay sync-progress-overlay">
          <div className="sync-progress-dialog">
            <div className="spinner" />
            <h3>Nextcloud Upload</h3>
            <p className="sync-progress-text">
              {ncProgress.current} / {ncProgress.total} Bilder
            </p>
            {ncProgress.fileName && (
              <p className="sync-progress-file">{ncProgress.fileName}</p>
            )}
            <div className="sync-progress-bar">
              <div
                className="sync-progress-fill"
                style={{
                  width: `${
                    ncProgress.total > 0
                      ? (ncProgress.current / ncProgress.total) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Nextcloud Upload Result Dialog */}
      {ncResult && (
        <div className="modal-overlay" onClick={() => setNcResult(null)}>
          <div className="description-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nextcloud Upload Ergebnis</h3>
              <button className="modal-close" onClick={() => setNcResult(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>{ncResult.succeeded.length}</strong> Bilder erfolgreich hochgeladen.
              </p>
              {ncResult.failed.length > 0 && (
                <>
                  <p style={{ color: "#ef4444" }}>
                    <strong>{ncResult.failed.length}</strong> Bilder fehlgeschlagen:
                  </p>
                  <ul className="sync-error-list">
                    {ncResult.failed.map((f, idx) => (
                      <li key={idx}>
                        {f.image?.fileName || "Unbekannt"}: {f.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn save" onClick={() => setNcResult(null)}>
                Schliessen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default References;
